import { User, Device, AuthTokens, FirmwareRecord } from "../Types";
import { createDirectus, rest, refresh, authentication, logout } from '@directus/sdk';
import { saveValue, getValue, deleteValue } from "../Utils/Cache";
import moment from 'moment';

class DBManager {
    user: User | null = null;
    devices: Device[] = [];
    accessToken: string | null = null;
    apiUrl: string = process.env.API_URL ?? 'https://admin.modemaker.knog.com';
    client = createDirectus(this.apiUrl);
    authTokens: AuthTokens = {
        access: 'directus_session_token_',
        refresh: 'directus_refresh_token_',
        expires: 'directus_expires_at_'
    }

    constructor() {
        this.accessToken = getValue(this.authTokens.access);
    }

    async init() {
        // Check if the user is authenticated
        if(this.isAuthenticated()) {
            // Get the user data
            await this.getUser();
        }
    }

    async getUser() {
        // Get the user data
        return await this.request('/users/me')
        .then((result) => {
            this.user = {
                id: result.id,
                email: result.email,
                first_name: result.first_name,
                last_name: result.last_name,
                country: result.country
            }
            return true;
        }).catch((error) => {
            console.error(error);
            return false;
        });
    }

    async saveUser(user: User) {
        return await this.request(`/users/${user.id}`, 'PATCH', user)
        .then((result) => {
            this.user = user;
            return true;
        }).catch((error) => {
            console.error(error);
            return false;
        });
    }

    async auth(username: string, password: string) {
        // login using the authentication composable
        return await this.client.with(authentication('json')).login(username, password).then((result) => {
            saveValue(this.authTokens.access, result.access_token);
            saveValue(this.authTokens.refresh, result.refresh_token);
            saveValue(this.authTokens.expires, result.expires_at);
            this.accessToken = result.access_token;
            this.getUser();
            return true;
        }).catch((error) => {
            console.error(error);
            return false;
        });
    }

    async logout() {
        // logout using the authentication composable
        if(getValue(this.authTokens.refresh)) 
            await this.client.with(rest())
            .request(logout(getValue(this.authTokens.refresh) ?? '', 'json'))
            .catch((error) => {console.error(error);});
            
        this.clearCache();
    }

    async register(firstName: string, email: string, country: string, password: string) {
        // Register a new user
        return await this.send('/users', 'POST', {
            first_name: firstName,
            email: email,
            country: country,
            password: password,
            role: '15ee3aab-59d6-48d4-bc83-b52aa2ded9f7'
        }).then((result) => {
            return result;
        }).catch((error) => {
            console.error(error);
            return error;
        });
    }

    async getNewToken() {
        // refresh http request using a cookie
        await this.client.with(rest()).request(refresh('json', getValue(this.authTokens.refresh) ?? ''))
        .then((result) => {
            // Save the auth data to the cache
            this.accessToken = result.access_token;
            saveValue(this.authTokens.access, this.accessToken);
            saveValue(this.authTokens.refresh, result.refresh_token);
            if(result.expires_at) saveValue(this.authTokens.expires, result.expires_at);
            if(result.expires) {
                saveValue(this.authTokens.expires, (moment().unix() * 1000) + result.expires);
            }
        }).catch((error) => {
            // this.logout();
            console.error(error);
        });
    }

    async request(endpoint: string, method: string = "GET", body?: any) {
        const reponse = await fetch(this.apiUrl + endpoint, {
            headers: {
                'Content-Type': 'application/json',
                ...(this.accessToken ? {'Authorization': `Bearer ${this.accessToken}`} : {})
            },
            method: method,
            body: JSON.stringify(body)
        }).then((response) => (response.json()));
        return reponse.data ? reponse.data : reponse;
    }

    async send(endpoint: string, method: string = "POST", body?: any) {
        return await fetch(this.apiUrl + endpoint, {
            headers: {
                'Content-Type': 'application/json',
                ...(this.accessToken ? {'Authorization': `Bearer ${this.accessToken}`} : {})
            },
            method: method,
            body: JSON.stringify(body)
        })
    }

    isAuthenticated() {
        // Refresh the token if it has expired
        if(this.tokenExpired() && this.accessToken !== null) {
            this.getNewToken();
        }
        // Check if the user is authenticated
        return this.accessToken !== null;
    }

    tokenExpired() {
        // Check if the token has expired
        return moment().isAfter(moment.unix(parseInt(getValue(this.authTokens.expires) ?? '0') / 1000));
    }

    clearCache() {
        deleteValue(this.authTokens.access);
        deleteValue(this.authTokens.refresh);
        deleteValue(this.authTokens.expires);
        this.accessToken = null;
    }

    async getFirmwarePayload(firmware: FirmwareRecord) {
        if(!firmware.file) return null;
        const fileURL = `${this.apiUrl}/assets/${firmware.file}`;

        // Get file buffer
        return await fetch(fileURL, {
            headers: {
                ...(this.accessToken ? {'Authorization': `Bearer ${this.accessToken}`} : {})
            }
        });
    }

    log = async (message: string, verbose: boolean = true) => {
        if(verbose) console.log(message);
        this.send('/items/error_log', 'POST', {message})
    }
}

export default DBManager;